// AICA Sentry Integration
// Conditional Sentry integration for error tracking
// Falls back to console logging if Sentry is not configured

import type { BrowserOptions } from '@sentry/react';
import { maskReplayRecordingEvent, maskSentryBrowserEvent } from './sentry-event-mask';

// Track initialization state to prevent double initialization
let isInitializing = false;
let isInitialized = false;

// Last user passed to setUserContext. The @sentry/react chunk loads via an
// un-awaited dynamic import racing Clerk: if auth settles first, the
// setUser call would be silently dropped — replay it once init completes.
let lastUser: {
  userId: string | null;
  email: string | null;
  name: string | null;
} | null = null;

// A CI secret once contained a line break inside the DSN — Sentry rejects it
// and prod telemetry silently dies. Whitespace is never meaningful in a DSN.
function readDsn(): string {
  return (import.meta.env.VITE_SENTRY_DSN ?? '').replace(/\s+/g, '');
}

/**
 * Expected embedded (Bitrix24 iframe) session expiry.
 *
 * The embedded session has a finite TTL; when a long-open tab crosses it, in-flight
 * queries 401 all at once. That is a re-auth prompt shown to the user, not a bug —
 * so it must not reach Sentry as an error. This matches both report shapes: the
 * thrown `ApiError` (`captureException`) and the "API request failed" message whose
 * status/message ride in `extra` (`captureMessage`). Scoped to `/embedded` so
 * standalone 401s are unaffected. Exported for direct unit testing (the
 * `beforeSend` closure isn't).
 *
 * **The signal is the app's own decision, not the server's wording.** Matching on
 * text alone was measurably wrong: this filter was written against "Session
 * expired. Please re-authenticate through Bitrix24.", and production never emits
 * it. Over 7 days edge-api logged 41 × "[Embedded Session] Missing session cookie"
 * and 0 × expired/not-found, because the cookie's `maxAge` and the session row's
 * `expiresAt` are both one hour and are written together — the browser drops the
 * cookie at the instant the session lapses, so the server sees no cookie rather
 * than a stale one and answers "Missing session cookie. Please authenticate
 * through Bitrix24." (no "re-", no "expired"). Every real lapse therefore sailed
 * past the filter.
 *
 * `handledByEmbeddedRecovery` / `extra.embeddedRecoveryHandled` is set in
 * `fetch-utils.ts` exactly when `notifyEmbeddedSessionExpired()` took ownership,
 * i.e. when the app is already re-entering through the Bitrix24 entry point. An
 * embedded 401 that recovery DECLINED (no SDK, mint attempts spent) stays a
 * reported error on purpose: that tab is genuinely stuck, and it is the one
 * failure here worth waking someone for.
 *
 * The text patterns below are kept for the two messages the FRONTEND raises
 * itself (`EmbeddedOrgContext`, "reopen via Bitrix24"), which never pass through
 * the fetch layer and so carry no flag.
 */
export function isExpectedEmbeddedSessionExpiry(
  originalException: unknown,
  eventMessage: string | undefined,
  extra: Record<string, unknown> | undefined,
  pathname: string
): boolean {
  if (!pathname.startsWith('/embedded')) return false;

  if (
    (originalException as { handledByEmbeddedRecovery?: boolean } | null | undefined)
      ?.handledByEmbeddedRecovery === true ||
    extra?.embeddedRecoveryHandled === true
  ) {
    return true;
  }

  const status =
    (originalException as { status?: number } | null | undefined)?.status ??
    (typeof extra?.status === 'number' ? (extra.status as number) : undefined);

  const text = [
    originalException instanceof Error ? originalException.message : '',
    eventMessage ?? '',
    typeof extra?.message === 'string' ? (extra.message as string) : '',
  ].join(' ');

  if (/re-authenticate through Bitrix24|reopen via (?:Bitrix24|Uspacy)/i.test(text))
    return true;
  return status === 401 && /session expired|invalid session/i.test(text);
}

/**
 * Initialize Sentry if DSN is provided
 * Returns true if Sentry is initialized, false otherwise
 *
 * The DSN is a build-time constant (Vite inlines import.meta.env.*): if it is
 * empty here it stays empty for the lifetime of the bundle — no retries.
 */
export async function initSentry(): Promise<boolean> {
  // Prevent double initialization
  if (isInitialized) {
    return true;
  }
  if (isInitializing) {
    // Wait for ongoing initialization
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (isInitialized) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (!isInitializing) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 50);
    });
  }

  const dsn = readDsn();

  // Check if DSN is provided and not empty
  if (dsn === '') {
    // Log warning in development - this usually means VITE_SENTRY_DSN is missing from .env.local
    if (import.meta.env.DEV) {
      console.warn(
        '[Sentry] VITE_SENTRY_DSN not set in .env.local - error tracking disabled'
      );
    }
    return false;
  }

  return await initSentryInternal(dsn);
}

/**
 * Pages whose URL itself is a bearer credential, so Session Replay must not
 * run on them at all.
 *
 * `/invitations/<token>` is the org-invitation acceptance page: the path
 * segment IS the 7-day credential to join an organization. Every other
 * Sentry channel masks it (`maskSentryBrowserEvent` on error and transaction
 * events, `maskReplayRecordingEvent` on Replay's Custom sub-events), but
 * Replay's rrweb `Meta` event — emitted with every full DOM snapshot and
 * carrying `location.href` verbatim — reaches NO hook: the SDK applies
 * `beforeAddRecordingEvent` only when `isCustomEvent(event)` is true
 * (`@sentry/replay/build/npm/cjs/index.js`, `maybeApplyCallback`), and `Meta`
 * is rrweb type 4, not 5. Nothing downstream can redact it either — the
 * recording is an opaque rrweb payload by then.
 *
 * So the fix is upstream of the recording: never construct the integration on
 * these routes. Not `history.replaceState` to scrub the address bar, which
 * fails twice over — `@tanstack/history` PATCHES `window.history.replaceState`
 * to notify the router (`createBrowserHistory`), so rewriting the URL makes
 * the router re-match a route that no longer exists and unmounts the page;
 * and even done before the router boots it would race Replay's first snapshot
 * (Sentry loads via an un-awaited dynamic import) and break reload, which is
 * the invitee's only retry.
 *
 * Reachability check that makes an init-time pathname test sufficient rather
 * than a best-effort: nothing in the SPA links to `/invitations/$token` — the
 * page is only ever entered by a fresh load from the emailed link, so the
 * pathname at init IS the pathname of the page that shows the token.
 */
export function isCredentialBearingPath(pathname: string): boolean {
  return pathname.startsWith('/invitations/');
}

/**
 * Builds the `Sentry.init()` options object. Extracted into its own
 * exported function — rather than inlined into `initSentryInternal` — so
 * `integrations`/`beforeSend`/`beforeSendTransaction` can be unit-tested
 * directly against the real `@sentry/react` module (see
 * `sentry-build-options.test.ts`). Before this extraction NOTHING exercised
 * this object: `initSentry` is called exactly once, from `main.tsx`, and no
 * test imported or invoked it — the whole activation path (which integration
 * survives the filter, which callback reaches Replay, whether `beforeSend`
 * really returns a masked event) was pinned only by manual reading. Review
 * round 1, I2: `docs/audit/2026-07-31-remediation-ws3-ui-sentry-review.md`.
 *
 * `pathname` is a parameter rather than a `window.location` read inside the
 * closure because the decision it drives (see `isCredentialBearingPath`) has
 * to be made ONCE, at init, and be testable without redefining `location`.
 */
export function buildSentryOptions(
  Sentry: typeof import('@sentry/react'),
  dsn: string,
  pathname: string
): BrowserOptions {
  return {
    dsn,
    // Keep automatic IP/user collection disabled; explicit scrubbed context
    // is sufficient for diagnostics and avoids exporting tenant PII.
    sendDefaultPii: false,
    // A FUNCTION form of `integrations` REPLACES the default set instead
    // of merging with it (`@sentry/core/build/cjs/integration.js:20-36`);
    // the previous array form kept every default alongside ours. Used
    // here to drop `HttpContext`: it copies the full page href
    // (`window.location.href`, including any secret in the path/query)
    // onto `event.request` for every error event, AND writes `url.full`
    // onto every non-http.client span
    // (`@sentry/browser/.../integrations/httpcontext.js:9-38`) —
    // independent of anything `maskSentryBrowserEvent` cleans up
    // afterward. Removing the integration is a structural fix (the URL is
    // never written) rather than another downstream mask.
    // See docs/audit/2026-07-31-egress-ui-worker-sentry.md §3 (B1).
    // It also removes `User-Agent`/browser-OS identification from error
    // events — restored below via `setContext('browser_client', …)`
    // instead of keeping the integration, because the integration's
    // `preprocessEvent` is the actual URL-writing carrier (review I4).
    integrations: (defaultIntegrations) => [
      ...defaultIntegrations.filter((integration) => integration.name !== 'HttpContext'),
      Sentry.browserTracingIntegration(),
      // On a credential-bearing route the integration is not constructed at
      // all — see `isCredentialBearingPath`. Skipping construction, rather
      // than constructing and stopping it afterwards, is what makes this
      // deterministic: Replay takes its first DOM snapshot (with the raw
      // `location.href`) as soon as it starts.
      ...(isCredentialBearingPath(pathname)
        ? []
        : [
            Sentry.replayIntegration({
              // Session Replay's recording pipeline never runs through
              // `beforeSend`/`beforeSendTransaction`/event processors at all —
              // see `sentry-event-mask.ts`'s docblock for why. This is the only
              // hook Replay offers, and it only reaches `EventType.Custom`
              // recording sub-events (navigation spans, network spans, console
              // breadcrumbs) — not the periodic DOM-snapshot `Meta` event that
              // also carries the raw `location.href` (no hook reaches that one
              // in this SDK version; see the remediation report).
              beforeAddRecordingEvent: (event) =>
                maskReplayRecordingEvent(event) as typeof event,
            }),
          ]),
    ],
    // Tracing — full sampling in dev, light sampling in prod to avoid main-thread overhead
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
    // Session Replay
    // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysSessionSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
    // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    replaysOnErrorSampleRate: 1.0,
    // Release tracking
    environment: import.meta.env.MODE || 'development',
    // Error filtering (optional - filter out network errors in development)
    beforeSend(event, hint) {
      // Expected embedded session expiry is a user re-auth prompt, not an error.
      if (
        typeof window !== 'undefined' &&
        isExpectedEmbeddedSessionExpiry(
          hint.originalException,
          typeof event.message === 'string' ? event.message : undefined,
          event.extra,
          window.location.pathname
        )
      ) {
        if (import.meta.env.DEV) {
          console.warn('[Sentry] Dropped expected embedded session-expiry event');
        }
        return null;
      }

      // Filter out network errors in development
      if (import.meta.env.DEV) {
        const error = hint.originalException;
        if (error instanceof Error) {
          const message = error.message.toLowerCase();
          if (message.includes('network') || message.includes('failed to fetch')) {
            // Still log to console but don't send to Sentry in dev
            console.warn('[Sentry] Filtered out network error in development:', error);
            return null;
          }
        }
      }
      // Belt-and-suspenders: the global event processor (registered in
      // `initSentryInternal`, right after `Sentry.init`) already masked
      // this event before `beforeSend` ever saw it (verified against the
      // SDK — see `sentry-event-mask.ts`'s docblock). Calling the SAME
      // function again here is an idempotent no-op today and a second
      // line of defense if a future SDK version ever reorders
      // `prepareEvent()` relative to `processBeforeSend`.
      return maskSentryBrowserEvent(event);
    },
    beforeSendTransaction(event) {
      return maskSentryBrowserEvent(event);
    },
  };
}

/**
 * Internal Sentry initialization function
 */
async function initSentryInternal(dsn: string): Promise<boolean> {
  // Prevent double initialization
  if (isInitialized || isInitializing) {
    return isInitialized;
  }

  isInitializing = true;

  try {
    // Import @sentry/react for React app
    const Sentry = await import('@sentry/react');

    Sentry.init(
      buildSentryOptions(
        Sentry,
        dsn,
        typeof window === 'undefined' ? '' : window.location.pathname
      )
    );

    // Reaches error events, transaction events, AND the Replay envelope
    // event (`type: 'replay_event'`, carrying `urls[]`) — the one channel
    // `beforeSend`/`beforeSendTransaction` cannot see at all, because
    // Replay's `sendReplayRequest` calls the shared `prepareEvent()`
    // utility directly rather than going through `client.captureEvent`.
    // Registering here (isolation scope) rather than as an init option
    // because `Sentry.init` has no "global event processor" option — see
    // `sentry-event-mask.ts`'s docblock for the full proof, corroborated by
    // an integration test against the real SDK
    // (`sentry-event-processor-architecture.test.ts`). Activation itself —
    // that this line actually runs — is pinned by
    // `sentry-init-activation.test.ts` (review I2).
    Sentry.addEventProcessor(maskSentryBrowserEvent);

    // `HttpContext` (removed above) used to be the source of `User-Agent`
    // on error events. It's gone because its ACTUAL job — writing the page
    // URL onto `event.request` — was the leak; browser/OS identification
    // is not a secret and error triage needs it. Restored as a static
    // context (not tied to `event.request`, so it survives on error events
    // too, unlike the transaction-only `headers` path in
    // `maskSentryBrowserEvent`). Review I4.
    if (typeof navigator !== 'undefined') {
      Sentry.setContext('browser_client', { user_agent: navigator.userAgent });
    }

    // Store Sentry module globally for use in captureException
    (globalThis as { Sentry?: typeof Sentry }).Sentry = Sentry;

    isInitialized = true;
    isInitializing = false;

    // Replay user context that arrived before the module finished loading.
    if (lastUser) {
      setUserContext(lastUser.userId, lastUser.email, lastUser.name);
    }

    console.log('[Sentry] Initialized successfully');
    return true;
  } catch (error) {
    isInitializing = false;
    console.warn('[Sentry] Failed to initialize:', error);
    return false;
  }
}

/**
 * Capture exception to Sentry (if initialized) or console
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
  try {
    // Try to use Sentry if available
    const sentryModule = (globalThis as { Sentry?: typeof import('@sentry/react') })
      .Sentry;
    if (sentryModule?.captureException) {
      sentryModule.captureException(error, {
        contexts: {
          custom: context || {},
        },
        tags: {
          component: 'ErrorBoundary',
        },
      });
      return;
    }
  } catch (err) {
    console.warn('[Sentry] Failed to capture exception:', err);
  }

  // Fallback to console logging
  console.error('[Error Tracking] Exception:', error, context || {});
}

/**
 * Capture message to Sentry (if initialized) or console.
 * Optional tags/extra are attached to the event for filtering and triage.
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
): void {
  try {
    const sentryModule = (globalThis as { Sentry?: typeof import('@sentry/react') })
      .Sentry;
    if (sentryModule?.captureMessage) {
      sentryModule.captureMessage(message, {
        level,
        tags: context?.tags,
        extra: context?.extra,
      });
      return;
    }
  } catch (err) {
    console.warn('[Sentry] Failed to capture message:', err);
  }

  // Fallback to console logging
  const consoleMethod =
    level === 'error' ? console.error : level === 'warning' ? console.warn : console.info;
  consoleMethod(`[Error Tracking] ${level.toUpperCase()}:`, message);
}

/**
 * Set user context for Sentry
 */
export function setUserContext(
  userId: string | null,
  email: string | null,
  name: string | null
): void {
  // Remember the latest user so init can replay it if Sentry isn't ready yet.
  lastUser = { userId, email, name };
  try {
    const sentryModule = (globalThis as { Sentry?: typeof import('@sentry/react') })
      .Sentry;
    if (sentryModule?.setUser) {
      sentryModule.setUser({
        id: userId || undefined,
        email: email || undefined,
        username: name || undefined,
      });
      return;
    }
  } catch (err) {
    console.warn('[Sentry] Failed to set user context:', err);
  }

  // Log for debugging if Sentry not initialized
  if (import.meta.env.DEV) {
    console.log('[Sentry] User context set:', { userId, email, name });
  }
}

/**
 * Add breadcrumb for Sentry
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  try {
    const sentryModule = (globalThis as { Sentry?: typeof import('@sentry/react') })
      .Sentry;
    if (sentryModule?.addBreadcrumb) {
      sentryModule.addBreadcrumb({
        message,
        category,
        level,
        timestamp: Date.now() / 1000,
      });
      return;
    }
  } catch (err) {
    console.warn('[Sentry] Failed to add breadcrumb:', err);
  }
}
